import { useEffect, useRef, useState } from "react";
import { buttonClass } from "@/components/common/controls";
import { SectionCard } from "@/components/posting/parts";
import PlaceLink from "@/components/site/PlaceLink";
import { actionProps } from "@/lib/controlAnalytics";
import { useOpenTailored } from "@/lib/hooks/resume";
import type { ToolTailorStage, ToolTailorStep } from "@/lib/hooks/toolTailor";
import { ROUTES } from "@/lib/routes";
import { RESUME_EMPTY } from "@/lib/tools/tailorAnswer";

/** Past this a read has stopped being instant, and a press needs words. */
const READ_QUIET_MS = 400;

const SENDING = {
    title: "Sending this job to the AI tailor",
    line: "This can take a moment. Please stay on this page.",
};
const WRITING = {
    title: "AI is tailoring your resume",
    line: "You can stay here or leave. Your Applications page keeps it.",
};
const READY = "Your tailored resume is ready";
const NOT_YET = "Not tailored yet";

/**
 * While the saved resume is read. Most reads end first, so a new account
 * goes to the upload with nothing shown. It says nothing of a tailor,
 * since none has been asked for. It takes the cursor for the phone, whose
 * panel can be below the fold.
 */
function GettingReady() {
    const [shown, setShown] = useState(false);
    const words = useRef<HTMLParagraphElement>(null);
    useEffect(() => {
        const timer = setTimeout(() => setShown(true), READ_QUIET_MS);
        return () => clearTimeout(timer);
    }, []);
    useEffect(() => {
        if (shown) words.current?.focus();
    }, [shown]);
    if (!shown) return null;
    return (
        <SectionCard>
            <p
                ref={words}
                tabIndex={-1}
                className="m-0 scroll-mt-[148px] text-ui text-fg-muted outline-none"
            >
                Getting ready.
            </p>
        </SectionCard>
    );
}

/**
 * Where a press of Tailor stands: on its way, being written, written, or
 * why not. It says a tailor is at work only once one is. It takes the
 * cursor as the job goes out, since on a phone the panel can be below the
 * fold and a press must show something. After that it stays where it is,
 * and its live region says each change.
 */
export default function TailorStatus({
    step,
    stage,
    line,
    resumeId,
}: {
    step: ToolTailorStep;
    stage: ToolTailorStage | null;
    line: string | null;
    resumeId: string | null;
}) {
    const openResume = useOpenTailored();
    const heading = useRef<HTMLHeadingElement>(null);
    useEffect(() => {
        if (stage === "sending") heading.current?.focus();
    }, [stage]);
    if (stage === "reading") return <GettingReady />;
    if (step === "idle" || step === "upload") return null;
    const words = stage === "sending" ? SENDING : stage === "writing" ? WRITING : null;
    const title = words?.title ?? (step === "ready" ? READY : NOT_YET);
    const empty = line === RESUME_EMPTY;
    const away = empty ? ROUTES.RESUME : ROUTES.APPLICATIONS;

    return (
        <SectionCard className="gap-3">
            <div aria-live="polite" className="flex flex-col gap-3">
                <h2
                    ref={heading}
                    tabIndex={-1}
                    className="m-0 scroll-mt-[148px] font-display text-card-title font-bold tracking-[-0.01em] outline-none"
                >
                    {title}
                </h2>
                {words && (
                    <p className="m-0 text-ui text-fg-muted [text-wrap:pretty]">{words.line}</p>
                )}
                {line && <p className="m-0 text-ui text-fg [text-wrap:pretty]">{line}</p>}
            </div>
            {step === "ready" && resumeId && (
                <button
                    {...actionProps("tailored_pdf_open_clicked", { source: "TailorStatus" })}
                    type="button"
                    onClick={() => openResume.mutate(resumeId)}
                    className={buttonClass("primary", "tall", "w-full")}
                >
                    Open your resume
                </button>
            )}
            {step !== "tailoring" && (
                <PlaceLink
                    {...actionProps("navigation_clicked", {
                        source: "TailorStatus",
                        target: away,
                    })}
                    to={away}
                    className="inline-flex min-h-11 items-center self-center text-[13px] font-semibold text-brand-ink"
                >
                    {empty ? "Go to your resume" : "Go to Applications"}
                </PlaceLink>
            )}
        </SectionCard>
    );
}
